"use client"

import * as React from "react"
import { useBreakpoint } from "../../../../src/stubs/use-breakpoint"
import { cn } from '../../../../src/cn'
import { SidebarContext, type SidebarContextProps } from "./use-sidebar"

const SIDEBAR_COOKIE_NAME = "sidebar_state"
// const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7 // Unused - cookie handling not implemented
// const SIDEBAR_WIDTH = "16rem" // Unused - width set via CSS variable
// const SIDEBAR_WIDTH_MOBILE = "18rem" // Unused - mobile width not implemented
// const SIDEBAR_WIDTH_ICON = "3rem" // Unused - icon width not implemented
const SIDEBAR_KEYBOARD_SHORTCUT = "b"

type SidebarProviderProps = {
  children: React.ReactNode
  defaultOpen?: boolean
  className?: string
}

const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  SidebarProviderProps
>(({ children, defaultOpen = true, className, ...props }, ref) => {
  const { isMobile, isTablet } = useBreakpoint()
  const [openMobile, setOpenMobile] = React.useState(false)
  // On tablet, default to collapsed for better space usage, but allow expansion
  const [open, setOpen] = React.useState(isTablet ? false : defaultOpen)

  React.useEffect(() => {
    const value = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${SIDEBAR_COOKIE_NAME}=`))
      ?.split("=")[1]

    if (value) {
      setOpen(value === "true")
    }
  }, [])

  const toggleSidebar = React.useCallback(() => {
    if (isMobile) {
      setOpenMobile((open) => !open)
    } else {
      setOpen((open) => !open)
    }
  }, [isMobile])

  // On mobile, state must reflect openMobile so SidebarContent is visible in the sheet when open
  const state = (isMobile ? openMobile : open) ? "expanded" : "collapsed"

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        toggleSidebar()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [toggleSidebar])

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      openMobile,
      setOpenMobile,
      isMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, openMobile, setOpenMobile, isMobile, toggleSidebar]
  )

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        ref={ref}
        className={cn(
          "group/sidebar-wrapper min-h-svh w-full has-[[data-variant=inset]]:bg-sidebar",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  )
})

SidebarProvider.displayName = "SidebarProvider"

export { SidebarProvider }
